import React, { useContext, useEffect } from "react";
import { ChatbotContext } from "@/store/context/ChatbotContext.tsx";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/shadcn/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/shadcn/form";
import { Input } from "@/components/shadcn/input";

import { useTranslation } from "react-i18next";
import { saveSettings } from "@/store/chatbot.ts";
import { Link, useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/shadcn/card.tsx";
import { constants } from "@/constants";
import Loading from "@/components/Loading";
import { ExternalLink } from "@/components/ExternalLink.tsx";
import { CopyButton } from "@/components/shadcn-custom/copy-to-clipboard.tsx";
import { Switch } from "@/components/shadcn/switch.tsx";

const FormSchema = z.object({
  enable: z.boolean(),
  ignorePrevConversations: z.boolean(),
  disableIfHumanAnswered: z.boolean(),
  apiKey: z.string().or(z.literal("")),
  resendWebhook: z.string().url().or(z.literal("")),
});

export default function PactIntegration() {
  const { chatbotID } = useParams();
  const { t } = useTranslation();
  const { chatSettings, viewChatbot, invalidateChatbot, setSettings } =
    useContext(ChatbotContext);

  function onSubmit(data: z.infer<typeof FormSchema>) {
    let set = Object.assign({}, chatSettings);
    set.integrations.pact = data;
    saveSettings(chatbotID, set, invalidateChatbot);
  }

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      enable: chatSettings?.integrations.pact.enable,
      ignorePrevConversations: chatSettings?.integrations.pact.ignorePrevConversations,
      disableIfHumanAnswered: chatSettings?.integrations.pact.disableIfHumanAnswered,
      apiKey: chatSettings?.integrations.pact.apiKey,
      resendWebhook: chatSettings?.integrations.pact.resendWebhook,
    },
  });

  useEffect(() => {
    if (chatSettings) {
      // form.setValue("apiKey", chatSettings?.integrations.pact.apiKey);
      form.reset(chatSettings?.integrations.pact)
    }
  }, [chatSettings]);

  if (!viewChatbot || !chatSettings) {
    return <Loading />;
  }

  let webhookURL = `${constants.url.API_PREFIX}integrations/pact/webhook?apikey=${viewChatbot.apikey}`;

  return (
    <Card className="">
      <CardHeader>
        <CardTitle>Pact</CardTitle>
        <CardDescription>
          Агрегатор мессенджеров и соцсетей для общения с клиентами и продаж:
          AmoCRM, Bitrix24, Telegram, WhatsApp
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="w-full space-y-6"
          >
            <FormField
              control={form.control}
              name="enable"
              render={({field}) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                  <div className="space-y-0.5">
                    <FormLabel className="cursor-pointer">
                      Включить интеграцию
                    </FormLabel>
                    <FormDescription>
                      Принимать сообщения из Пакт и генерировать ИИ ответ
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <span className="inline-block font-semibold pt-2">
              Настройка интеграции:
            </span>

            <div>
              <div
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                1) Установите
                <ExternalLink href="https://msg.pact.im/project_settings/info">
                  в кабинете Pact
                </ExternalLink>
                в поле "Webhook URL" значение ниже.
                <br/>
                <span className="font-normal opacity-80">
                  Если на стороне Pact уже стоит URL от другой интеграции -
                  скопируйте этот URL в поле (4) (см. доп. настройки)
                </span>
              </div>
              <br/>

              {/*<Input placeholder=""*/}
              {/*       value={`${constants.url.API_PREFIX}integrations/pact/webhook?apikey=${viewChatbot.apikey}`} />*/}
              <div className="flex items-center">
                <div className="bg-slate-50 py-1 px-3 rounded-lg">{webhookURL}</div>
                <CopyButton
                  value={webhookURL}
                  variant="outline"
                  className="ml-1"
                />
              </div>
            </div>

            <FormField
              control={form.control}
              name="apiKey"
              render={({field}) => (
                <FormItem>
                  <FormLabel>
                    2) Скопируйте ключ доступа к API
                    <ExternalLink href="https://msg.pact.im/account">
                      из кабинета Pact
                    </ExternalLink>
                    в поле ниже
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="" {...field} />
                  </FormControl>
                  <FormDescription></FormDescription>
                  <FormMessage/>
                </FormItem>
              )}
            />

            <span className="inline-block font-semibold pt-2">
              Дополнительные настройки:
            </span>

            <div className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Если необходимо чтобы ИИ ассистент отвечал в определенное время
              -&nbsp;
              <Link className="underline" to={`/chatbot/${chatbotID}/settings`}>
                настройте расписание
              </Link>
            </div>

            <FormField
              control={form.control}
              name="ignorePrevConversations"
              render={({field}) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                  <div className="space-y-0.5">
                    <FormLabel className="cursor-pointer">
                      Не отвечать на ранее созданные диалоги
                    </FormLabel>
                    <FormDescription>
                      ИИ Ассистент не будет отвечать на раннее созданные диалоги до включения интеграции
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="disableIfHumanAnswered"
              render={({field}) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                  <div className="space-y-0.5">
                    <FormLabel className="cursor-pointer">
                      Отключать ИИ ответы при ответе менеджера
                    </FormLabel>
                    <FormDescription>
                      ИИ Ассистент отключится в диалоге если ответил менеджер
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="resendWebhook"
              render={({field}) => (
                <FormItem>
                  <FormLabel>
                    Дублирование вебхука<br/>
                    <span className="font-normal opacity-80">
                      платформа получит вебхук от Pact и также продублирует
                      запрос по URL ниже от ранее существующей интеграции
                    </span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="" {...field} />
                  </FormControl>
                  <FormDescription>
                    Проверку доставки вебхуков можно проверить на
                    <ExternalLink href="https://webhook-test.com">
                      https://webhook-test.com
                    </ExternalLink>
                  </FormDescription>
                  <FormMessage/>
                </FormItem>
              )}
            />

            <Button type="submit">{t("save")}</Button>
          </form>
        </Form>
      </CardContent>
      <CardFooter className="flex justify-between"></CardFooter>
    </Card>
  );
}
